/** ==========================================================================
 * @file  jogger.js
 */
import './jogger.css';

import React from 'react';
import ReactDOM from 'react-dom';
import Helpers from '@src/utils/helpers.js';
import immediateApi from '@vse/api/immediateApi';
import * as machinemotion from '@vse/api/machinemotion.js';
import * as machinelogic from '@vse/main';
import state from './state.js';
import { messageBox } from '@src/style/style';

const gDebounce = Helpers.Debouncer();
const DEBOUNCE_TIME = 350;

let selectors;
let gRobotJoggerWidget;

/**
 * @returns {() => void} Cleanup logic for when we leave the page.
 */
export function initializeJogger (robotAppsService) {
  // TODO GD Ideally we wouldn't have global variables like this
  gRobotJoggerWidget = undefined; // Making sure to reset this global variable
  selectors = robotAppsService.selectors;

  const joggerGUI = createJoggerGUI();
  const joggerState = new state.JoggerState();

  // Get configuration and modify axis accordingly
  initState(joggerGUI, joggerState, (configInstructions) => {
    const robotConfig = configInstructions.filter(
      (configInstruction) => configInstruction.name === 'robot',
    )[0];
    robotConfig && robotAppsService.send('ManualControl/pageLoad', { robotConfig });

    renderControllerSelector(joggerGUI, joggerState, configInstructions);
  });

  return () => {
    robotAppsService?.send('ManualControl/pageUnload');
    joggerState?.cleanup();
  };
}

function createJoggerGUI () {
  // Creating the GUI
  const lGuiContainer = $('#guiContainer').addClass('jogger__container');
  const lTabs = $('<div>')
    .addClass('jogger__tabs')
    .css({ marginBottom: '0px' });
  const lContent = $('<div>').addClass('jogger__content');
  const lLeftPane = $('<div>').addClass('jogger__left-pane');
  const lRightPane = $('<div>').addClass('jogger__right-pane');
  const lRestrictedViewContainer = $('<div>')
    .addClass('ml_disabled_editor')
    .hide();
  const lWarningMessage = $('<div>')
    .text(
      'Restricted view of this controller. Please connect to the parent controller.',
    )
    .css({ left: '155px' });

  lRightPane.ioUpdateCallback = undefined;
  lRightPane.brakeStatusUpdateCallback = undefined;

  /**
   * ------------------------------------
   * |        lTabs           |
   * |----------------------------------|
   * |       |                          |
   * |       |                          |
   * | pLeft |        pRight            |
   * |       |                          |
   * |       |                          |
   * ------------------------------------
   */

  lGuiContainer.append(
    lTabs,
    lContent.append(
      lLeftPane,
      lRightPane.append(lRestrictedViewContainer.append(lWarningMessage)),
    ),
  );

  return {
    content: lContent,
    tabs: lTabs,
    leftPane: lLeftPane,
    rightPane: lRightPane,
    resetView: function () {
      lLeftPane.empty();
      lRightPane.empty();
      lRightPane.append(lRestrictedViewContainer);
    },
    resetRightPane: function () {
      lRightPane.empty();
      lRightPane.append(lRestrictedViewContainer);
      lRightPane.ioUpdateCallback = undefined;
      lRightPane.brakeStatusUpdateCallback = undefined;
      gRobotJoggerWidget &&
        ReactDOM.unmountComponentAtNode(gRobotJoggerWidget[0]);
    },
    restrictView: function () {
      lRightPane.css({ pointerEvents: 'none' });
      lRestrictedViewContainer.show();
    },
    enableView: function () {
      lRightPane.css({ pointerEvents: 'auto' });
      lRestrictedViewContainer.hide();
    },
  };
}

/**
 * Loads the machine motion configuration and populates the state object.
 */
function initState (pJoggerGUI, pJoggerState, pCallback) {
  // Fetch the Library to search for #global application
  machinemotion.library(function (pData) {
    if (pData && pData.status == undefined && pData.includes('#global')) {
      // Fetch the the #global application
      machinemotion.application('#global', function (pLibrary) {
        let lStartup;
        if (pLibrary && pLibrary.sequences && pLibrary.sequences.length > 0) {
          lStartup = pLibrary.sequences.find(pSequence => pSequence.name === '#startup');
          if (lStartup && lStartup.instructions) {
            // Set the parent
            const lParentControllerId = lStartup.parameters.parentController;
            pJoggerState.setParentController(lParentControllerId);

            // Sort instructions to the front to guarantee that they are added first.
            lStartup.instructions.sort(function (pFirst, pSecond) {
              if (
                pFirst.name === 'controller' &&
                pSecond.name === 'controller'
              ) {
                // Sort the parent controller to the front of the list
                if (
                  pFirst.parameters.uuid ===
                  pJoggerState.getParentControllerId()
                ) {
                  return -1;
                }
                else if (
                  pSecond.parameters.uuid ===
                  pJoggerState.getParentControllerId()
                ) {
                  return 1;
                }
                else {
                  return 0;
                }
              }
              if (pFirst.name === 'controller') {
                return -1;
              }
              else if (pSecond.name === 'controller') {
                return 1;
              }
              else return 0;
            });

            lStartup.instructions.forEach(function (pInst) {
              let lController;
              if (!pInst.name) {
                return;
              }

              switch (pInst.name) {
                case 'controller': {
                  pJoggerState.addController(pInst);
                  break;
                }
                case 'axis': {
                  lController = pJoggerState.getController(
                    pInst.parameters.controllerId,
                  );
                  if (!lController) {
                    console.error(
                      'Failed to resolve the controller for axis instruction',
                      pInst,
                    );
                    break;
                  }

                  lController.addAxis(pInst);
                  break;
                }
                case 'pneumaticAxis': {
                  lController = pJoggerState.getController(
                    pInst.parameters.controllerId,
                  );
                  if (!lController) {
                    console.error(
                      'Failed to resolve the controller for pneumatic instruction',
                      pInst,
                    );
                    break;
                  }

                  lController.addPneumatic(pInst);
                  break;
                }
                case 'alias': {
                  lController = pJoggerState.getController(
                    pInst.parameters.controllerId,
                  );
                  if (!lController) {
                    console.error(
                      'Failed to resolve the controller for io instruction',
                      pInst,
                    );
                    break;
                  }

                  lController.addIo(pInst);
                  break;
                }
              }
            });
          }
        }

        pJoggerState.subscribeToIOState(function (pIOState) {
          if (pJoggerGUI.rightPane.ioUpdateCallback) {
            pJoggerGUI.rightPane.ioUpdateCallback(pIOState);
          }
        });

        pJoggerState.subscribeToBrakeState(function (pBrakeStatus) {
          if (pJoggerGUI.rightPane.brakeStatusUpdateCallback) {
            pJoggerGUI.rightPane.brakeStatusUpdateCallback(pBrakeStatus);
          }
        });

        machinemotion.getConfiguredNetworkStatus((pNetworkStatus) => {
          // Set the host
          pJoggerState.setHostController(pNetworkStatus.hostController);
          // Update the availability of controllers
          Object.keys(pNetworkStatus.configuredControllers).forEach(
            (pControllerUuid) => {
              const lController = pJoggerState.getController(pControllerUuid);
              if (lController) {
                lController.mIsControllerAvailable =
                  pNetworkStatus.configuredControllers[
                    pControllerUuid
                  ].reachable;
              }
            },
          );

          // Finished initializing
          pCallback(lStartup.instructions);
        });
      });
    }
    else {
      console.error('Failed to retrieve the configuration.');
    }
  });
}

/**
 * Create the controller tabs on the top of the view. When clicked, these controller tabs
 * populate the sidebar, which in turn populate the right pane.
 */
function renderControllerSelector (joggerGUI, joggerState, configInstructions) {
  const lTabs = joggerGUI.tabs;
  const lTabContainer = $('<div>')
    .addClass('ml_controller_tab_container')
    .css({ maxWidth: '100%' });
  lTabs.addClass('ml_controller_container').append(lTabContainer);

  joggerState.listControllerStates().forEach(function (pController, pIndex) {
    const lControllerItem = $('<div>').addClass('ml_controller_tab');
    const lPluggedInIcon = $('<div>', {
      text: 'Plugged into:',
      class: 'ml_controller_plugged_in_indicator',
    });
    const lControllerName = $('<label>')
      .text(pController.getFormattedName())
      .attr('title', pController.getName());
    const lUndetectedIcon = $('<i>')
      .addClass('icon-danger')
      .css('color', '#D9534F')
      .css('padding-left', '5px');

    lTabContainer.append(
      lControllerItem.append(lPluggedInIcon, lControllerName, lUndetectedIcon),
    );

    const renderActuatorView = function (pAxis, pSidebarElement) {
      $('.jogger-left-pane__robot-item--selected').removeClass(
        'jogger-left-pane__robot-item--selected jogger-left-pane__robot-item--selected-axis',
      );
      $('.jogger-left-pane__actuator-item--selected').removeClass(
        'jogger-left-pane__actuator-item--selected jogger-left-pane__actuator-item--selected-axis',
      );
      pSidebarElement.addClass(
        'jogger-left-pane__actuator-item--selected jogger-left-pane__actuator-item--selected-axis',
      );

      joggerGUI.resetRightPane();
      pAxis.fetchAxisInitialData((pInitialData) => {
        joggerGUI.rightPane.append(actuatorView(joggerGUI, joggerState, pAxis, pInitialData));
      });
    };

    const renderRobotView = function (pSidebarElement) {
      $('.jogger-left-pane__robot-item--selected').removeClass(
        'jogger-left-pane__robot-item--selected jogger-left-pane__robot-item--selected-axis',
      );
      $('.jogger-left-pane__actuator-item--selected').removeClass(
        'jogger-left-pane__actuator-item--selected jogger-left-pane__actuator-item--selected-axis',
      );
      pSidebarElement.addClass(
        'jogger-left-pane__robot-item--selected jogger-left-pane__robot-item--selected-axis',
      );

      joggerGUI.resetRightPane();
      joggerGUI.rightPane.append(robotView());
    };

    const renderIOView = function (pSidebarElement) {
      $('.jogger-left-pane__robot-item--selected').removeClass(
        'jogger-left-pane__robot-item--selected jogger-left-pane__robot-item--selected-axis',
      );
      $('.jogger-left-pane__actuator-item--selected').removeClass(
        'jogger-left-pane__actuator-item--selected jogger-left-pane__actuator-item--selected-axis',
      );
      pSidebarElement.addClass('jogger-left-pane__actuator-item--selected');

      joggerGUI.resetRightPane();
      joggerGUI.rightPane.append(IOView(joggerGUI, joggerState));
    };

    const renderErrorView = function (pErrorHTML) {
      joggerGUI.resetRightPane();
      const lWarningMessageContainer = $('<div>').addClass(
        'jogger__warning-message',
      );
      const lWarningMessage = $(pErrorHTML);

      joggerGUI.rightPane.append(
        lWarningMessageContainer.append(lWarningMessage),
      );
    };

    const renderRightPane = {
      renderActuatorView,
      renderRobotView,
      renderIOView,
      noActuatorView: () => {
        renderErrorView(
          '<p>Add an actuator to your controller<br/> in the <b>Configuration</b> tab.</p>',
        );
      },
      noDetectedControllerView: () => {
        renderErrorView(
          '<p>Cannot connect to this controller.<br/> Please fix your <b>Network</b> settings.</p>',
        );
      },
    };

    const lUpdateUI = function () {
      lTabContainer
        .find('.ml_controller_tab_selected')
        .removeClass('ml_controller_tab_selected');
      lControllerItem.addClass('ml_controller_tab_selected');
      joggerGUI.resetView();
      joggerState.isReadonly()
        ? joggerGUI.restrictView()
        : joggerGUI.enableView();
      renderJoggerSidebar(joggerGUI, joggerState, pController, renderRightPane, configInstructions);
    };

    const lSelectController = function () {
      if (joggerState.getSelectedControllerId() == pController.getId()) {
        return;
      }
      joggerState.resetSelectedController();
      joggerState.setSelectedControllerId(pController.getId());
      if (pController.isAvailable()) {
        pController.fetchSmartDriveStatus(() => {
          // we have to do this check because on long loads,
          // the user can request a different tab other than
          // the first controller and this would cause a bad ui state
          if (joggerState.getSelectedControllerId() === pController.getId()) {
            lUpdateUI();
          }
        });
      }
      else {
        lUpdateUI();
      }
    };

    if (pIndex === 0) {
      lSelectController();
    }

    lControllerItem.on('click', lSelectController);

    // Initialize
    lPluggedInIcon.hide();
    lUndetectedIcon.hide();

    // Show plugged into icon if the controller is the host controller
    if (pController.getId() === joggerState.getHostControllerId()) {
      lPluggedInIcon.show();
    }

    // Show error icon if the controller is not available
    if (!pController.isAvailable()) {
      lUndetectedIcon.show();
    }
  });
}

function renderJoggerSidebar (
  pJoggerGUI,
  pJoggerState,
  pController,
  pRenderRightPane,
  configInstructions,
) {
  // HTML
  const lActuators = $('<div>').addClass(
    'jogger-left-pane__actuator-container',
  );
  const lActuatorHeader = $('<div>')
    .addClass('jogger-left-pane__header')
    .text('Available Actuators');
  const lActuatorList = $('<div>').addClass('jogger-left-pane__actuators');
  const lRobots = $('<div>').addClass('jogger-left-pane__robot-container');
  const lRobotHeader = $('<div>')
    .addClass('jogger-left-pane__header')
    .text('Available Robots');
  const lRobotList = $('<div>').addClass('jogger-left-pane__robots');
  const lIOs = $('<div>').addClass('jogger-left-pane__io-container');
  const lIOHeader = $('<div>')
    .addClass('jogger-left-pane__header')
    .text('Available Control Modules');
  const lIOButton = $('<div>').addClass('jogger-left-pane__io-button');
  const lIOIcon = $('<i>').addClass('icon-input-output');
  const lIOButtonText = $('<span>').text('Digital Inputs/Outputs');

  pJoggerGUI.leftPane.append(
    lActuators.append(lActuatorHeader, lActuatorList),
    lRobots.append(lRobotHeader, lRobotList),
    lIOs.append(lIOHeader, lIOButton.append(lIOIcon, lIOButtonText)),
  );

  lIOButton.on('click', function () {
    gRobotJoggerWidget && ReactDOM.unmountComponentAtNode(gRobotJoggerWidget[0]);

    if (pController.isAvailable()) {
      const lSelectedAxis = pJoggerState.getSelectedAxis();
      if (lSelectedAxis && lSelectedAxis.getId() === undefined) {
        return;
      }

      pJoggerState.getSelectedController().selectAxis(undefined); // clear polling loops

      pRenderRightPane.renderIOView(lIOButton);
    }
  });

  // Controller View Initialization
  if (pController.isAvailable()) {
    const lAxisList = pController.listAxes();
    const lRobotList = configInstructions.filter(
      (configInstruction) => configInstruction.name === 'robot',
    );
    if (pController.listAxes().length !== 0) {
      lAxisList.forEach((pAxis) => {
        addAxisToSidebar(pAxis);
      });
      lActuatorList.find('.jogger-left-pane__actuator-item').eq(0).click();
    }
    if (lRobotList.length !== 0) {
      lRobotList.forEach((pRobot) => {
        addRobotToSidebar(pRobot);
      });
      lRobotList.find('.jogger-left-pane__robot-item').eq(0).click();
    }
    if (pController.listAxes().length === 0 && lRobotList.length === 0) {
      pRenderRightPane.noActuatorView();
    }
  }
  else {
    pRenderRightPane.noDetectedControllerView();
  }

  // Helper Functions
  function formatAxisTypeForJogger (pAxis) {
    const lAxisType = Helpers.formatAxisType(pAxis.getType());
    const lTypeThatSupportGearbox = [
      'belt',
      'enclosed_timing_belt',
      'heavy_duty_roller_conveyor',
      'rack_and_pinion',
      'rack_and_pinion_v2',
      'belt_rack',
      'indexer',
    ];
    if (pAxis.getMultiplier() !== 1) {
      return lAxisType + ' with Gearbox';
    }
    else if (lTypeThatSupportGearbox.includes(pAxis.getType())) {
      return lAxisType + ' without Gearbox';
    }
    return lAxisType;
  }

  function getDriveListString (pAxis) {
    const lDrivesList = pAxis.getDrivesList();
    return `Drive${lDrivesList.length > 1 ? 's' : ''} ${lDrivesList.join(
      ', ',
    )}`;
  }

  function addAxisToSidebar (pAxis) {
    const lWidget = $('<div>').addClass('jogger-left-pane__actuator-item');
    const lAxisIconContainer = $('<div>').addClass(
      'jogger-left-pane__actuator-icon',
    );
    const lAxisIcon = $('<i>').addClass(
      Helpers.getAxisIconFromType(pAxis.getType()),
    );
    const lAxisInfo = $('<div>');
    const lAxisName = $('<div>')
      .text(pAxis.getName())
      .addClass('jogger-left-pane__actuator-title');
    const lAxisType = $('<div>')
      .text(formatAxisTypeForJogger(pAxis))
      .addClass('jogger-left-pane__sub-title');
    const lDriveNumber = $('<div>')
      .text(getDriveListString(pAxis))
      .addClass('jogger-left-pane__sub-title');

    lWidget.on('click', function () {
      gRobotJoggerWidget && ReactDOM.unmountComponentAtNode(gRobotJoggerWidget[0]);

      const lSelectedAxis = pJoggerState.getSelectedAxis();
      if (lSelectedAxis && lSelectedAxis.getId() === pAxis.getId()) {
        return;
      }

      pJoggerState.getSelectedController().selectAxis(pAxis.getId()); // clear polling loops

      pRenderRightPane.renderActuatorView(pAxis, lWidget);
    });

    lActuatorList.append(
      lWidget.append(
        lAxisIconContainer.append(lAxisIcon),
        lAxisInfo.append(lAxisName, lAxisType),
        lDriveNumber,
      ),
    );
  }

  /**
   * @typedef {Object} RobotConfig
   * @property {{
   *   friendlyName: String
   *   robotId: String
   *   robotType: String
   *   controllerId: String
   * }} parameters
   */

  /**
   * @param {RobotConfig} pRobot
  */
  function addRobotToSidebar (pRobot) {
    const {
      manualControlRobotJogger: { robotTypeFormatUR },
    } = machinelogic;

    const lWidget = $('<div>').addClass('jogger-left-pane__robot-item');
    const lRobotIconContainer = $('<div>').addClass(
      'jogger-left-pane__robot-icon',
    );
    const lRobotIcon = $('<i>').addClass('icon-robot-assets');
    const lRobotInfo = $('<div>');
    const lRobotName = $('<div>')
      .text(pRobot.parameters.friendlyName)
      .addClass('jogger-left-pane__robot-title');
    const lRobotSubtitle = $('<div>')
      .text(robotTypeFormatUR(pRobot.parameters.robotType))
      .addClass('jogger-left-pane__sub-title');

    lWidget.on('click', function () {
      pJoggerState.getSelectedController().selectAxis();

      pRenderRightPane.renderRobotView(lWidget);
    });

    lRobotList.append(
      lWidget.append(
        lRobotIconContainer.append(lRobotIcon),
        lRobotInfo.append(lRobotName, lRobotSubtitle),
      ),
    );
  }
}

function actuatorView (pJoggerGUI, pJoggerState, pAxis, pInitialData) {
  let lJogDistance = 250; // actuator relative jog distance (in mm)
  let lJogToPosition = 100; // position to move the axis to (in mm)

  // micro states - can be moved to state.js
  let lIsInMotion = true;
  let lIsHoming = false;

  isAxisMotionComplete();
  // -- Constants
  const gMinSpeed = 0.001; // in mm/s
  const gMinAcceleration = 0.001; // in mm/s^2
  const gMaxAcceleration = 2000; // in mm/s^2
  const gMinJog = 0.001; // in mm
  const gMaxJog = 500; // in mm

  // -- Enum
  const gMotionTypeEnum = Object.freeze({
    Home: 'Home',
    End: 'End',
    Absolute: 'Absolute',
    Relative: 'Relative',
  });

  const lActuatorView = $('<div>').addClass('jogger-right-pane__actuator-view');

  const lActuatorSettingsHeader = $('<div>')
    .addClass('jogger-right-pane__header')
    .text('Actuator');

  const lActuatorSettings = $('<div>').addClass('jogger-right-pane__actuator-settings');

  const lSpeedControl = getSpeedControlWidget();
  const lAccelerationControl = getAccelerationControlWidget();
  const lRotationAndSensors = getRotationAndSensorsWidget();
  const lPositionAndBrakes = getPositionAndBrakesWidget();

  const lJoggerHeader = $('<div>')
    .addClass('jogger-right-pane__header')
    .text('Move');

  const lJogger = $('<div>').addClass('jogger-right-pane__actuator-jogger');

  const lMoveRelativeControls = getMoveRelativeControlWidget();
  const lMoveHomeEndAndAbsoluteControls = getMoveHomeEndAndAbsoluteControlWidget();

  lActuatorView.append(
    $('<div>').append(
      lActuatorSettingsHeader,
      lActuatorSettings.append(
        lSpeedControl,
        lAccelerationControl,
        lRotationAndSensors,
        lPositionAndBrakes,
      ),
    ),
    $('<div>').append(
      lJoggerHeader,
      lJogger.append(
        lMoveRelativeControls,
        lMoveHomeEndAndAbsoluteControls,
      ),
    ),
  );

  function getControlWidget (
    pLabel,
    pUnits,
    pMinValue,
    pMaxValue,
    pDefault,
    pOnChange,
  ) {
    let lSliderMaxValue = 2500;
    if (pMaxValue !== undefined) { lSliderMaxValue = pMaxValue; }

    const lWidget = $('<div>').addClass('jogger-slide-widget__container');
    // lContainer
    // |_ lRow
    //   |_ Label
    //   |_ lInputContainer
    // |_ lSliderContainer

    const lRow = $('<div>').addClass('jogger-slide-widget__row');

    const lLabel = $('<label>').text(pLabel);
    // Control Input
    const lInputContainer = $('<div>').addClass(
      'jogger_right_pane_value_viewer',
    );
    const lInput = $('<input>').val(pDefault).attr('type', 'number');
    const lUnits = $('<span>').html(pUnits);

    lInputContainer.append(lInput, lUnits);

    // Control Slider Input
    const lSliderContainer = $('<div>').addClass('jogger_right_pane_slider');
    const lSliderInput = $('<input>')
      .attr('type', 'range')
      .attr('min', pMinValue)
      .attr('max', lSliderMaxValue + 1)
      .appendTo(lSliderContainer);
    const lThumb = $('<span>')
      .appendTo(lSliderContainer)
      .append($('<div>'), $('<div>'));
    let lThumbWidth = 0;

    lSliderInput.on('input', function () {
      const lWidth = $(this).width();
      const lValue = parseInt(this.value, 10);
      const lMax = parseInt(this.max, 10);
      const lMin = parseInt(this.min, 10);
      const lPosition = (lValue * (lWidth - lThumbWidth)) / (lMax - lMin);

      lThumb.css('left', lPosition + 'px');
    });

    // Trigger the initial input once we are sure that the slider has mounted.
    // This ensures that we have the right width.
    setTimeout(function () {
      lThumbWidth = lThumb.width();
      lSliderInput.val(pDefault);
      lSliderInput.trigger('input');
    }, 0);

    // On Change Slider
    lSliderInput.on('change', (event) => {
      let lValue = event.target.value;
      if (!isNaN(lValue)) {
        lValue = lValue > 1 ? Math.round(lValue) : Helpers.formatFloat(lValue);
        lInput.val(lValue);
        pOnChange(lValue);
      }
    });

    let oldValue = '';
    // saves the last value in case we need to revert
    lInput.on('focus', () => {
      oldValue = lInput.val();
    });

    // On Change Input
    lInput.on('change', function (event) {
      let lValue = parseFloat(event.target.value);
      const isEmptyValue = isNaN(lValue);
      const isInValid = lValue < pMinValue || (pMaxValue && lValue > pMaxValue);

      if (isEmptyValue || isInValid) {
        event.target.value = oldValue;
        messageBox('Invalid value. Your changes have been reverted', 'warning');
      }

      if (isEmptyValue) { return; }
      if (isInValid) { lValue = oldValue; }

      lValue = Helpers.formatFloat(lValue);
      lInput.val(lValue);

      lSliderInput.val(lValue).trigger('input');
      pOnChange(lValue);
    });

    lWidget.append(lRow.append(lLabel, lInputContainer), lSliderContainer);

    return lWidget;
  }

  function getSpeedControlWidget () {
    const lDefaultSpeed = pInitialData.speed;

    const lOnSpeedChange = (lSpeed) => {
      const lUuid = pAxis.getId();
      immediateApi
        .setSpeed(lUuid, lSpeed)
        .then(function () {
          pAxis.setMaxSpeed(lSpeed);
        })
        .catch(function () {
          messageBox(
            'Something went wrong while trying to set speed.',
            'error',
          );
        });
    };

    return getControlWidget(
      'Max Speed',
      'mm/sec',
      gMinSpeed,
      pAxis.getMaxAllowedSpeed(),
      lDefaultSpeed,
      lOnSpeedChange,
    );
  }

  function getAccelerationControlWidget () {
    const lDefaultAcceleration = pInitialData.acceleration;

    const lOnAccelerationChange = (lAcceleration) => {
      const lUuid = pAxis.getId();
      immediateApi
        .setAcceleration(lUuid, lAcceleration)
        .then(function () {
          pAxis.setMaxAcceleration(lAcceleration);
        })
        .catch(function () {
          messageBox(
            'Something went wrong while trying to set acceleration.',
            'error',
          );
        });
    };

    return getControlWidget(
      'Max Acceleration',
      'mm/sec<sup>2</sup>',
      gMinAcceleration,
      gMaxAcceleration,
      lDefaultAcceleration,
      lOnAccelerationChange,
    );
  }

  function getRotationAndSensorsWidget () {
    // lWidget
    // |_ RotationRow
    //    |_ lRotationLabel
    //    |_ lRotation
    // |_ EndStopStatusRow
    //    |_ lEndStopStatusLabel
    //    |_ lEndStopStatusContainer
    //      |_ HomeSensor
    //        |_ LED
    //        |_ Label
    //      |_ EndSensor
    //        |_ LED
    //        |_ Label

    function getRotationString () {
      const lRotationList = pAxis.getRotationList().map(function (pRotation) {
        switch (pRotation) {
          case 1:
            return 'Normal';
          case -1:
            return 'Reverse';
          default:
            return 'Unknown';
        }
      });
      return lRotationList.join(', ');
    }

    const lWidget = $('<div>').addClass(
      'jogger-rotation-and-sensor-widget__container',
    );

    const lRotationRow = $('<div>').addClass(
      'jogger-rotation-and-sensor-widget__row',
    );
    const lRotationLabel = $('<div>').text('Rotation');
    const lRotation = $('<div>').text(getRotationString());

    const lEndStopRow = $('<div>').addClass(
      'jogger-rotation-and-sensor-widget__row',
    );
    const lEndStopLabel = $('<div>').text('Activated Endstop Sensor');
    const lEndStopStatusContainer = $('<div>');

    const lHomeSensor = $('<div>').addClass(
      'jogger-rotation-and-sensor-widget__end-sensor-container',
    );
    const lHomeSensorLED = $('<div>').addClass(
      'jogger-rotation-and-sensor-widget__led',
    );
    const lHomeSensorLabel = $('<div>').text(
      `Home (Port ${pInitialData.endstopConfig.home})`,
    );

    const lEndSensor = $('<div>').addClass(
      'jogger-rotation-and-sensor-widget__end-sensor-container',
    );
    const lEndSensorLED = $('<div>').addClass(
      'jogger-rotation-and-sensor-widget__led',
    );
    const lEndSensorLabel = $('<div>').text(
      `End (Port ${pInitialData.endstopConfig.end})`,
    );

    lWidget.append(
      lRotationRow.append(lRotationLabel, lRotation),
      lEndStopRow.append(
        lEndStopLabel,
        lEndStopStatusContainer.append(
          lHomeSensor.append(lHomeSensorLED, lHomeSensorLabel),
          lEndSensor.append(lEndSensorLED, lEndSensorLabel),
        ),
      ),
    );

    // Initialize the LED's
    let lReceivedEndstopStatus = false;
    lHomeSensorLED.css({ visibility: 'hidden' });
    lEndSensorLED.css({ visibility: 'hidden' });

    pAxis.mController.startPollingEndStop(function (pEndStopStates) {
      if (!lReceivedEndstopStatus) {
        lHomeSensorLED.css({ visibility: 'visible' });
        lEndSensorLED.css({ visibility: 'visible' });
      }
      lReceivedEndstopStatus = true;

      const lAxisEndStopStates = pEndStopStates[pAxis.getNumber()];
      const lLedActiveClassName =
        'jogger-rotation-and-sensor-widget__led--active';
      lAxisEndStopStates.home
        ? lHomeSensorLED.addClass(lLedActiveClassName)
        : lHomeSensorLED.removeClass(lLedActiveClassName);
      lAxisEndStopStates.end
        ? lEndSensorLED.addClass(lLedActiveClassName)
        : lEndSensorLED.removeClass(lLedActiveClassName);
    });

    return lWidget;
  }

  function getPositionAndBrakesWidget () {
    // lWidget
    // |_ lPositionRow
    //    |_ lPositionLabel
    //    |_ lPosition
    // |_ lBrakeControlRow
    //    |_ lBrakeControlLabel
    //    |_ lBrakeControlContainer

    const lWidget = $('<div>').addClass(
      'jogger-position-and-brake-widget__container',
    );

    const lPositionRow = $('<div>').addClass(
      'jogger-position-and-brake-widget__row',
    );
    const lPositionLabel = $('<div>').text('Position');
    const lPosition = $('<div>').text(pInitialData.position + ' mm');

    const lBrakeControlRow = $('<div>').addClass(
      'jogger-position-and-brake-widget__row',
    );
    const lBrakeControlLabel = $('<div>').text('Brake Control');
    const lBrakeControl = $('<div>').addClass(
      'jogger-position-and-brake-widget__brake-button-container',
    );
    const lLockBrakesButton = $('<button>').addClass(
      'jogger-position-and-brake-widget__brake-button',
    );
    const lUnlockBrakesButton = $('<button>').addClass(
      'jogger-position-and-brake-widget__brake-button',
    );
    const lNoBrakesContainer = $('<div>')
      .text('Not Configured')
      .addClass('jogger-position-and-brake-widget__no-brakes');

    lLockBrakesButton.on('click', function () {
      immediateApi.lockBrakes(pAxis.getId()).catch(function () {
        messageBox(
          'Something went wrong while trying to lock brakes.',
          'error',
        );
      });
    });
    lUnlockBrakesButton.on('click', getUnlockBrakeFunction());

    let lIsUserTryingtoUnlock = false;

    lWidget.append(
      lPositionRow.append(lPositionLabel, lPosition),
      lBrakeControlRow.append(
        lBrakeControlLabel,
        !pAxis.hasBrakes()
          ? lNoBrakesContainer
          : lBrakeControl.append(lLockBrakesButton, lUnlockBrakesButton),
      ),
    );

    const lOnBrakeStatusUpdate = function (pBrakeStatus) {
      if (lIsUserTryingtoUnlock) {
        return;
      }
      const lSelectedBrakeClass =
        'jogger-position-and-brake-widget__brake-button--selected';

      // reset state
      lLockBrakesButton.removeAttr('disabled');
      lUnlockBrakesButton.removeAttr('disabled');
      lLockBrakesButton.removeClass(lSelectedBrakeClass);
      lUnlockBrakesButton.removeClass(lSelectedBrakeClass);
      lLockBrakesButton.text('Lock');
      lUnlockBrakesButton.text('Unlock');

      switch (pBrakeStatus) {
        case 'Locked':
          lLockBrakesButton.attr('disabled', true);
          lLockBrakesButton.addClass(lSelectedBrakeClass);
          lLockBrakesButton.text('Locked');
          break;
        case 'Unlocked':
          lUnlockBrakesButton.attr('disabled', true);
          lUnlockBrakesButton.addClass(lSelectedBrakeClass);
          lUnlockBrakesButton.text('Unlocked');
          break;
        case 'Unknown':
          break;
      }
    };

    pAxis.mController.startPollingPosition(function (positionInfo) {
      const lDriveLetter = Helpers.convert(pAxis.getNumber())
        .toLetter()
        .toUpperCase();
      if (positionInfo[lDriveLetter] != undefined && !lIsHoming) {
        lPosition.text(Helpers.formatFloat(positionInfo[lDriveLetter]) + ' mm');
      }
      else {
        lPosition.text('...');
      }
    });

    // Helper functions
    function getUnlockBrakeFunction () {
      const lResetUnlockBrakeButtonState = function () {
        lIsUserTryingtoUnlock = false;
        window.removeEventListener('click', lOnWindowClick);
        document.removeEventListener('keydown', lOnKeypress);
        lUnlockBrakesButton
          .text('Unlock')
          .removeClass('jogger-position-and-brake-widget__confirm-unlock');
        lLockBrakesButton.show();
      };

      const lOnWindowClick = function () {
        lResetUnlockBrakeButtonState();
      };

      const lOnKeypress = function (pEvent) {
        if (pEvent.keyCode === 27) {
          // 27 is the keycode for escape
          pEvent.preventDefault();
          lResetUnlockBrakeButtonState();
        }
      };

      return function (pEvent) {
        pEvent.stopPropagation();

        if (lIsUserTryingtoUnlock) {
          lResetUnlockBrakeButtonState();
          immediateApi.unlockBrakes(pAxis.getId()).catch(function () {
            messageBox(
              'Something went wrong while trying to unlock brakes.',
              'error',
            );
          });
        }
        else {
          lIsUserTryingtoUnlock = true;
          lUnlockBrakesButton
            .text('Click to Confirm Unlock')
            .addClass('jogger-position-and-brake-widget__confirm-unlock');
          lLockBrakesButton.hide();
          window.addEventListener('click', lOnWindowClick);
          document.addEventListener('keydown', lOnKeypress);
        }
      };
    }

    lWidget.onBrakeStatusUpdateCallback = lOnBrakeStatusUpdate;

    return lWidget;
  }

  function updateMotionButtons () {
    const lDisabled =
      (pAxis.hasBrakes() && pAxis.getBrakeStatus() === 'Locked') || lIsInMotion;
    lJogger.find('.motion-button').each(function (_, pElement) {
      $(pElement).attr('disabled', lDisabled);
    });
  }

  function isAxisMotionComplete () {
    let stopPolling = false;
    let pollingTimeout;

    const lSelectedAxis = pJoggerState.getSelectedAxis();
    if (!lSelectedAxis) {
      return;
    }

    immediateApi
      .isAxisMotionComplete(lSelectedAxis.getId())
      .then(pStatus => {
        if (stopPolling) {
          return;
        }

        // If the user selects a different axis, we don't want retry or update the UI
        if (lSelectedAxis !== pJoggerState.getSelectedAxis()) {
          return;
        }

        if (pStatus.isComplete === false) {
          // keep checking every second if motion is complete. I don't think there's a need to check more than this.
          lIsInMotion = true;
          updateMotionButtons();
          pollingTimeout = setTimeout(isAxisMotionComplete, 1000);
        }
        else {
          lIsInMotion = false;
          updateMotionButtons();
        }
      })
      .catch(function () {
        console.error(
          'Something went wrong while trying to check for motion completion.',
        );
      });

    $('#guiContainer').on('remove', () => {
      stopPolling = true;
      clearTimeout(pollingTimeout);
    });
  }

  function getMoveRelativeControlWidget () {
    const lOnJogDistanceChange = (pDistance) => {
      lJogDistance = pDistance;
    };
    const lWidget = getControlWidget(
      'Jog Increment',
      'mm',
      gMinJog,
      gMaxJog,
      lJogDistance,
      lOnJogDistanceChange,
    );
    const lJogButtons = $('<div>').addClass(
      'jogger-slide-widget__row jogger-slide-widget__row--center',
    );
    const lJogNegative = $('<button>').addClass(
      'icon-arrow-left jogger-icon-button jogger-icon-button-minus motion-button',
    );
    const lJogPositive = $('<button>').addClass(
      'icon-arrow-right jogger-icon-button jogger-icon-button-plus motion-button',
    );

    lJogNegative.on('click', function () {
      lIsInMotion = true;
      updateMotionButtons();
      immediateApi
        .moveRelative(pAxis.getId(), -1 * lJogDistance)
        .then(function () {
          lIsInMotion = false;
          updateMotionButtons();
        })
        .catch(function () {
          messageBox(
            'Something went wrong while trying to move.',
            'error',
          );
          lIsInMotion = false;
          updateMotionButtons();
        });
    });

    lJogPositive.on('click', function () {
      lIsInMotion = true;
      updateMotionButtons();
      immediateApi
        .moveRelative(pAxis.getId(), lJogDistance)
        .then(function () {
          lIsInMotion = false;
          updateMotionButtons();
        })
        .catch(function () {
          messageBox(
            'Something went wrong while trying to move.',
            'error',
          );
          lIsInMotion = false;
          updateMotionButtons();
        });
    });

    lWidget.append(lJogButtons.append(lJogNegative, lJogPositive));

    return lWidget;
  }

  function getMoveHomeEndAndAbsoluteControlWidget () {
    // lWidget
    // |_ lTabs
    //    |_ lAbsolute
    //    |_ lHome
    //    |_ lEnd
    // |_ lPosition
    //    |_ lLabel
    //    |_ lPositionContainer
    //      |_ lPositionInput
    //      |_ lPositionUnits

    // |_ lMotionButtons
    //    |_ lMoveButton
    //    |_ lStopButton

    let lSelectedMotionType = gMotionTypeEnum.Absolute;

    const lWidget = $('<div>').addClass('jogger-absolute-home-end__container');

    const lTabs = $('<div>').addClass('jogger-absolute-home-end__tabs');
    const lAbsolute = $('<div>')
      .text('Absolute')
      .addClass(
        'jogger-absolute-home-end__tab jogger-absolute-home-end__tab--selected',
      );
    const lHome = $('<div>')
      .text('Home')
      .addClass('jogger-absolute-home-end__tab');
    const lEnd = $('<div>')
      .text('End')
      .addClass('jogger-absolute-home-end__tab');

    const lPosition = $('<div>').addClass('jogger-absolute-home-end__position');
    const lLabel = $('<div>').text('Position Command').css('font-size', '17px');

    const lPositionContainer = $('<div>').addClass(
      'jogger_right_pane_value_viewer',
    );
    const lPositionInput = $('<input>')
      .val(lJogToPosition)
      .attr('type', 'number');
    const lPositionUnits = $('<span>').html('mm');

    const lMotionButtons = $('<div>').addClass(
      'jogger-absolute-home-end__controls',
    );
    const lMoveButton = $('<button>').addClass(
      'icon-play jogger-icon-button motion-button',
    );
    const lStopButton = $('<button>').addClass(
      'icon-stop jogger-icon-button stop-button',
    );

    const lSelectedTabClass = 'jogger-absolute-home-end__tab--selected';

    // Helper Function
    function resetTabsAndInput () {
      lAbsolute.removeClass(lSelectedTabClass);
      lHome.removeClass(lSelectedTabClass);
      lEnd.removeClass(lSelectedTabClass);
      lSelectedMotionType === gMotionTypeEnum.Absolute
        ? lPosition.css('visibility', 'visible')
        : lPosition.css('visibility', 'hidden');
      switch (lSelectedMotionType) {
        case gMotionTypeEnum.Absolute:
          lAbsolute.addClass(lSelectedTabClass);
          break;
        case gMotionTypeEnum.Home:
          lHome.addClass(lSelectedTabClass);
          break;
        case gMotionTypeEnum.End:
          lEnd.addClass(lSelectedTabClass);
          break;
      }
    }

    lAbsolute.on('click', function () {
      lSelectedMotionType = gMotionTypeEnum.Absolute;
      resetTabsAndInput();
    });

    lHome.on('click', function () {
      lSelectedMotionType = gMotionTypeEnum.Home;
      resetTabsAndInput();
    });

    lEnd.on('click', function () {
      lSelectedMotionType = gMotionTypeEnum.End;
      resetTabsAndInput();
    });

    // On Change Input
    lPositionInput.on('change', function (event) {
      let lPosition = event.target.value;
      if (!isNaN(lPosition) && lPosition !== '') {
        lPosition = Helpers.formatFloat(lPosition);
        lPositionInput.val(lPosition);
        lJogToPosition = lPosition;
      }
      else {
        event.target.value = lJogToPosition;
        messageBox(
          'Invalid value. Your changes have been reverted',
          'warning',
        );
      }
    });

    lMoveButton.on('click', function () {
      lIsInMotion = true;
      updateMotionButtons();
      switch (lSelectedMotionType) {
        case gMotionTypeEnum.Absolute:
          immediateApi
            .moveToPosition(pAxis.getId(), lJogToPosition)
            .then(() => {
              lIsInMotion = false;
              updateMotionButtons();
            })
            .catch(function () {
              messageBox(
                'Something went wrong while trying to move.',
                'error',
              );
              lIsInMotion = false;
              updateMotionButtons();
            });
          break;
        case gMotionTypeEnum.Home:
          lIsHoming = true;
          immediateApi
            .home(pAxis.getId())
            .then(() => {
              lIsInMotion = false;
              lIsHoming = false;
              updateMotionButtons();
              // update the position here
            })
            .catch(function () {
              messageBox(
                'Something went wrong while trying to move.',
                'error',
              );
              lIsInMotion = false;
              lIsHoming = false;
              updateMotionButtons();
            });
          break;
        case gMotionTypeEnum.End:
          immediateApi
            .end(pAxis.getId())
            .then(() => {
              lIsInMotion = false;
              updateMotionButtons();
            })
            .catch(function () {
              messageBox(
                'Something went wrong while trying to move.',
                'error',
              );
              lIsInMotion = false;
              updateMotionButtons();
            });
          break;
      }
    });

    lStopButton.on(
      'click',
      gDebounce(function () {
        immediateApi
          .stop(pAxis.mController.getId())
          .then(function () {
            lIsInMotion = false;
            updateMotionButtons();
          })
          .catch(function () {
            messageBox(
              'Something went wrong while trying to stop.',
              'error',
            );
          });
      }, DEBOUNCE_TIME),
    );

    lWidget.append(
      lTabs.append(lAbsolute, lHome, lEnd),
      lPosition.append(
        lLabel,
        lPositionContainer.append(lPositionInput, lPositionUnits),
      ),
      lMotionButtons.append(lMoveButton, lStopButton),
    );

    return lWidget;
  }

  const onBrakeStatusUpdate = function (pBrakeStatus) {
    // Update Brake Buttons
    lPositionAndBrakes.onBrakeStatusUpdateCallback(pBrakeStatus);
    // Update Motion Buttons
    updateMotionButtons();
  };

  // Attach the brake callback if the axis has brakes configured
  pJoggerGUI.rightPane.brakeStatusUpdateCallback = onBrakeStatusUpdate;

  // Update brake UI wrt the current brake status
  onBrakeStatusUpdate(pAxis.getBrakeStatus());

  return lActuatorView;
}

function IOView (joggerGUI, joggerState) {
  const SdkDeviceType = Object.freeze({
    Empty: undefined,
    IoExpander: 'io-expander',
    PushButton: 'push-button',
    PowerSwitch: 'power-switch',
  });
  const gPushButtonStatesEnum = Object.freeze({ Pushed: 1, Released: 0 });
  const gPowerSwitchStatesEnum = Object.freeze({ On: 'on', Off: 'off' });

  // HTML
  const lWidget = $('<div>').addClass('jogger-io__container ml_scrollable');
  const lInputsHeader = $('<div>')
    .addClass('jogger-right-pane__header')
    .text('Inputs');
  const lInputsList = $('<div>');
  const lOutputsHeader = $('<div>')
    .addClass('jogger-right-pane__header')
    .text('Outputs');
  const lOutputsList = $('<div>');
  const lWarningMessageContainer = $('<div>')
    .addClass('jogger__warning-message')
    .css({ height: '100%' });
  const lWarningMessage = $(
    '<p>No <b>input/output module</b> detected.<br>Plug them into the CONTROL ports<br>of your <b>MachineMotion controller.</b></p>',
  );

  lWidget.append(
    $('<div>').append(lInputsHeader, lInputsList),
    $('<div>').append(lOutputsHeader, lOutputsList),
    lWarningMessageContainer.append(lWarningMessage),
  );

  // Initialize

  // Attach the IO callback
  joggerGUI.rightPane.ioUpdateCallback = function (pIOState) {
    updateIoGui(pIOState);
  };

  // Update the IO GUI with the current IO state
  updateIoGui(joggerState.getSelectedController().getIOState());

  function updateIoGui (pIOState) {
    // Reset View
    resetView();

    if (pIOState && pIOState.some((pModule) => pModule.isAvailable)) {
      lInputsHeader.show();
      lOutputsHeader.show();

      pIOState.forEach(function (pStatusItem) {
        if (!pStatusItem.isAvailable) return;

        switch (pStatusItem.deviceType) {
          case SdkDeviceType.IoExpander: {
            createIOModuleInputGui(pStatusItem);
            createIOModuleOutputGui(pStatusItem);
            break;
          }
          case SdkDeviceType.PushButton: {
            createPushButtonGui(pStatusItem);
            break;
          }
          case SdkDeviceType.PowerSwitch: {
            createPowerswitchModuleGui(pStatusItem);
            break;
          }
        }
      });
    }
    else {
      lWarningMessageContainer.css({ display: 'flex' });
    }
  }

  // Widgets
  function getSwitchWidget (pIo, pin) {
    const Container = $('<div class="switch">');
    const Controller = $(
      '<input type="checkbox" id="IO_Expander_Output-' +
        pIo.device +
        '-' +
        pin +
        '">',
    ).appendTo(Container);
    Controller.prop('checked', pIo.outputs[pin]);
    $('<div class="slider_switch">').appendTo(Container);
    $(
      '<label class="switch" for="IO_Expander_Output-' +
        pIo.device +
        '-' +
        pin +
        '">',
    ).appendTo(Container);
    Controller.on('change', function (event) {
      immediateApi
        .writeDigitalOutputRaw(
          joggerState.getSelectedControllerId(),
          pIo.device,
          pin,
          event.target.checked ? 1 : 0,
        )
        .catch(function () {
          console.error(
            'Something went wrong while trying to set the Digital Output State.',
          );
        });
    });
    return Container;
  }

  function createIOModuleInputGui (pIoExpander) {
    // Get friendly names
    const lFriendlyNames = ['', '', '', ''];

    joggerState
      .getSelectedController()
      .listInputs()
      .forEach(function (pInputState) {
        if (
          pInputState.getDevice() == pIoExpander.device &&
          pInputState.getDeviceType() == 'digitalIOModule'
        ) {
          lFriendlyNames[pInputState.getPin()] =
            '(' + pInputState.getName() + ')';
        }
      });

    const lInputWidget = $(
      '<div class="jogger_io_right_pane_item" id="IOExpander-' +
        pIoExpander.device +
        '" >',
    );
    const lIcon = $('<div class="jogger_io_icon icon-arrow-to-the-right">');
    const lDetails = $('<div class="jogger_io_details">');

    $('<div>')
      .text('Digital IO Module ' + pIoExpander.device)
      .appendTo(lDetails);

    $('<div id="version_info">')
      .text('HW ' + pIoExpander.hwVersion + ' FW ' + pIoExpander.swVersion)
      .appendTo(lDetails);

    const lLeftPane = $('<div class="jogger_io_left_pane">').append([
      lIcon,
      lDetails,
    ]);
    const lRightPane = $('<div class="jogger_io_right_pane">');

    lFriendlyNames.forEach(function (name, pin) {
      const Container = $(
        '<div id="IO_Expander_Input-' + pIoExpander.device + '-' + pin + '">',
      )
        .css({
          display: 'flex',
          flexDirection: 'Column',
          alignItems: 'center',
          justifyContent: 'flex-end',
        })
        .appendTo(lRightPane);
      $('<div>')
        .text(name || '')
        .css({
          fontSize: '11px',
          fontWeight: '400',
          fontStyle: 'italic',
          lineHeight: '1',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          width: '100px',
          textOverflow: 'ellipsis',
          textAlign: 'center',
        })
        .appendTo(Container);
      $('<div>')
        .text('Pin ' + pin)
        .css({ fontSize: '16px', fontWeight: '400' })
        .appendTo(Container);
      $('<div>')
        .text(pIoExpander.pinStates[pin])
        .css({ fontSize: '28px', fontWeight: '400', marginTop: '5px' })
        .appendTo(Container);
    });

    lInputWidget.append([lLeftPane, lRightPane]);
    lInputsList.append(lInputWidget);
  }

  function createIOModuleOutputGui (pIoExpander) {
    // Get friendly names
    const lFriendlyNames = ['', '', '', ''];
    joggerState
      .getSelectedController()
      .listOutputs()
      .forEach(function (pOutputState) {
        if (
          pOutputState.getDevice() == pIoExpander.device &&
          pOutputState.getDeviceType() == 'digitalIOModule'
        ) {
          lFriendlyNames[pOutputState.getPin()] =
            '(' + pOutputState.getName() + ')';
        }
      });

    const lOutputWidget = $(
      '<div class="jogger_io_right_pane_item" id="IOExpander-' +
        pIoExpander.device +
        '" >',
    );
    const lIcon = $('<div class="jogger_io_icon icon-arrow-from-left">');
    const lDetails = $('<div class="jogger_io_details">');

    $('<div>')
      .text('Digital IO Module ' + pIoExpander.device)
      .appendTo(lDetails);

    $('<div id="version_info">')
      .text('HW ' + pIoExpander.hwVersion + ' FW ' + pIoExpander.swVersion)
      .appendTo(lDetails);

    const lLeftPane = $('<div class="jogger_io_left_pane">').append([
      lIcon,
      lDetails,
    ]);
    const lRightPane = $('<div class="jogger_io_right_pane">');

    lFriendlyNames.forEach(function (name, pin) {
      const Container = $('<div>')
        .css({
          display: 'flex',
          flexDirection: 'Column',
          alignItems: 'center',
          justifyContent: 'flex-end',
        })
        .appendTo(lRightPane);
      $('<div>')
        .text(name || '')
        .css({
          fontSize: '11px',
          fontWeight: '400',
          fontStyle: 'italic',
          lineHeight: '1',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          width: '100px',
          textOverflow: 'ellipsis',
          textAlign: 'center',
        })
        .appendTo(Container);
      $('<div>')
        .text('Pin ' + pin)
        .css({ fontSize: '16px', fontWeight: '400' })
        .appendTo(Container);
      getSwitchWidget(pIoExpander, pin).appendTo(Container);
    });

    lOutputWidget.append([lLeftPane, lRightPane]);
    lOutputsList.append(lOutputWidget);
  }

  function createPushButtonGui (pPushButton) {
    // Create GUI
    const lPushButtonInputWidget = $(
      '<div class="jogger_io_right_pane_item" id="PushButton-' +
        pPushButton.device +
        '" >',
    );

    const lIcon = $('<div class="jogger_io_icon icon-arrow-to-the-right">');
    const lDetails = $('<div class="jogger_io_details">');

    $('<div>')
      .text('Push Button Module ' + pPushButton.device)
      .appendTo(lDetails);

    $('<div id="version_info">')
      .text('HW ' + pPushButton.hwVersion + ' FW ' + pPushButton.swVersion)
      .appendTo(lDetails);

    const lLeftPane = $('<div class="jogger_io_left_pane">').append([
      lIcon,
      lDetails,
    ]);
    const lRightPane = $('<div class="jogger_io_right_pane">');

    // Get friendly names
    const lButtonNames = ['Black Button', 'White Button'];

    joggerState
      .getSelectedController()
      .listInputs()
      .forEach(function (pInputState) {
        if (
          pInputState.getDevice() == pPushButton.device &&
          pInputState.getDeviceType() == 'pushButtonModule'
        ) {
          lButtonNames[pInputState.getPin()] = pInputState.getName();
        }
      });

    lButtonNames.forEach(function (friendlyname, pin) {
      const lStatusText =
        pPushButton.pinStates[pin] === gPushButtonStatesEnum.Pushed
          ? 'Pushed'
          : 'Released';
      const lStatusColor =
        pPushButton.pinStates[pin] === gPushButtonStatesEnum.Pushed
          ? '#1AC876'
          : 'grey';
      const lPushCountText = pPushButton.pushCount[pin];

      const Container = $('<div>').css({ width: '180px' }).appendTo(lRightPane);
      const StatusContainer = $('<div>').css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '90%',
        justifyContent: 'flex-end',
        paddingBotton: '5%',
        width: '39%',
      });
      $('<div>')
        .text('STATUS')
        .css({ fontWeight: '400', fontSize: '12px', color: '1C344F' })
        .appendTo(StatusContainer);
      $('<div id="PushButton-' + pPushButton.device + '-' + pin + '-status">')
        .text(lStatusText)
        .css({ fontWeight: '600', fontSize: '18px', color: lStatusColor })
        .appendTo(StatusContainer);
      const CountContainer = $('<div>').css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '90%',
        justifyContent: 'flex-end',
        paddingBotton: '5%',
      });
      $('<div>')
        .text('PUSH COUNTER')
        .css({ fontWeight: '400', fontSize: '12px', color: '1C344F' })
        .appendTo(CountContainer);
      $('<div id="PushButton-' + pPushButton.device + '-' + pin + '-count">')
        .text(lPushCountText)
        .css({ fontWeight: '600', fontSize: '18px', color: 'black' })
        .appendTo(CountContainer);
      $('<div>')
        .css({
          height: '100%',
          width: '100%',
          borderRadius: '3px',
          display: 'flex',
          justifyContent: 'space-evenly',
          border: '3px solid #EFF2F7',
        })
        .append([StatusContainer, CountContainer])
        .appendTo(Container);
      const HeaderContainer = $('<div>')
        .css({
          height: '12px',
          padding: '0 15px',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          top: '-75px',
        })
        .appendTo(Container);
      const Header = $('<div>')
        .css({
          padding: '0 5%',
          background: 'white',
          display: 'flex',
          alignItems: 'center',
        })
        .appendTo(HeaderContainer);
      $('<div>')
        .css({
          height: '15px',
          width: '15px',
          borderRadius: '7.5px',
          background: pin == 0 ? 'black' : 'white',
          boxShadow: pin == 1 ? '0px 0px 7px rgba(0, 0, 0, 0.19)' : 'none',
        })
        .appendTo(Header);
      $('<div>')
        .text(friendlyname)
        .css({
          marginLeft: '4px',
          fontSize: '14px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          maxWidth: '129px',
          textOverflow: 'ellipsis',
        })
        .appendTo(Header);
    });

    lPushButtonInputWidget.append([lLeftPane, lRightPane]);
    lInputsList.append(lPushButtonInputWidget);
  }

  function createPowerswitchModuleGui (powerswitchModule) {
    // Get friendly name
    let lFriendlyName = '';
    joggerState
      .getSelectedController()
      .listOutputs()
      .forEach(function (pOutputState) {
        if (
          pOutputState.getDevice() == powerswitchModule.device &&
          pOutputState.getDeviceType() == 'powerSwitchModule'
        ) {
          lFriendlyName = '(' + pOutputState.getName() + ')';
        }
      });

    const lPowerSwitchWidget = $(
      '<div class="jogger_io_right_pane_item" id="PowerSwitch-' +
        powerswitchModule.device +
        '" >',
    );
    const lIcon = $('<div class="jogger_io_icon icon-arrow-from-left">');
    const lDetails = $('<div class="jogger_io_details">');

    $('<div>')
      .text('Power Switch Module ' + powerswitchModule.device)
      .appendTo(lDetails);

    $('<div id="version_info">')
      .text(
        'HW ' +
            powerswitchModule.hwVersion +
            ' FW ' +
            powerswitchModule.swVersion,
      )
      .appendTo(lDetails);

    const lLeftPane = $('<div class="jogger_io_left_pane">').append([
      lIcon,
      lDetails,
    ]);
    const lRightPane = $('<div class="jogger_io_right_pane">').css({
      justifyContent: 'flex-start',
      paddingLeft: '0px',
    });
    const Container = $('<div>')
      .css({
        display: 'flex',
        flexDirection: 'Column',
        alignItems: 'center',
        justifyContent: 'flex-end',
      })
      .appendTo(lRightPane);
    $('<div>')
      .text(lFriendlyName)
      .css({
        fontSize: '11px',
        fontWeight: '400',
        fontStyle: 'italic',
        lineHeight: '1',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        width: '100px',
        textOverflow: 'ellipsis',
        textAlign: 'center',
      })
      .appendTo(Container);
    $('<div>')
      .text('Switch')
      .css({ fontSize: '16px', fontWeight: '400' })
      .appendTo(Container);
    const Switch = $('<div class="switch">').appendTo(Container);
    const Controller = $(
      '<input type="checkbox" id="PS_Module-' +
        powerswitchModule.device +
        '_Switch">',
    ).appendTo(Switch);
    $('<div class="slider_switch slider_power_switch">').appendTo(Switch);
    $(
      '<label class="switch" for="PS_Module-' +
        powerswitchModule.device +
        '_Switch">',
    ).appendTo(Switch);

    Controller.prop(
      'checked',
      powerswitchModule.powerSwitchState == gPowerSwitchStatesEnum.On,
    );

    Controller.on('change', function (event) {
      immediateApi
        .writePowerSwitchRaw(
          joggerState.getSelectedControllerId(),
          powerswitchModule.device,
          event.target.checked
            ? gPowerSwitchStatesEnum.On
            : gPowerSwitchStatesEnum.Off,
        )
        .catch(function () {
          console.error(
            'Something went wrong while trying to set the Power Switch State.',
          );
        });
    });

    lPowerSwitchWidget.append([lLeftPane, lRightPane]);
    lOutputsList.append(lPowerSwitchWidget);
  }

  // Helper functions
  function resetView () {
    lInputsList.empty();
    lOutputsList.empty();
    lInputsHeader.hide();
    lOutputsHeader.hide();
    lWarningMessageContainer.css({ display: 'none' });
  }

  return lWidget;
}

function robotView () {
  const {
    manualControlRobotJogger: { ManualControlRobotJoggerContainer },
  } = machinelogic;
  gRobotJoggerWidget = $('<div class="ml_document v_layout spaced">');
  const robotJoggerContainerComponent = React.createElement(
    ManualControlRobotJoggerContainer,
    {
      shouldDisplayRobotJoggerRobotDisconnectedOverlay:
        selectors.shouldDisplayRobotJoggerRobotDisconnectedOverlay,
      shouldDisplayRobotJoggerRobotNotReadyOverlay:
        selectors.shouldDisplayRobotJoggerRobotNotReadyOverlay,
    },
  );
  ReactDOM.render(robotJoggerContainerComponent, gRobotJoggerWidget[0]);

  return gRobotJoggerWidget;
}
